import { Suspense, lazy, useState } from 'react';
import type { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';

import type { GameCatalogEntry } from '@perplexicon/api-client';

import { Button, Dialog, ErrorState, Icon, Input, LoadingState } from '../../components/index.js';
import { LOBBY_CONFIG_REGISTRY } from '../../games/registry.js';
import type { LobbyConfigFormProps } from '../../games/types.js';
import { isActiveGameConflict } from '../../lib/active-game-conflict.js';
import { getErrorMessage } from '../../lib/errors.js';
import { useCreateLobbySession } from '../../lib/games.js';
import { modeLabel } from './mode-labels.js';

export interface CreateLobbyDialogProps {
  game: GameCatalogEntry;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Passed down from the page so a start/join refused with
   * `ACTIVE_SESSION_EXISTS` (APP_SPEC §21.1) opens the shared
   * resume-or-abandon dialog instead of this dialog's own error banner.
   * Returns `true` when it handled the error.
   */
  onActiveGameConflict?: (error: unknown, retry: () => void) => boolean;
}

const MAX_INVITEES = 9;

/**
 * `React.lazy()` must be called once per component, not on every render
 * (same constraint `apps/web/src/features/games/GameHostPage.tsx`'s
 * `resolveGameComponent` documents) — this module-level cache calls
 * `lazy()` exactly once per `gameId` and reuses the same component
 * reference afterward.
 */
const lazyConfigForms = new Map<string, ComponentType<LobbyConfigFormProps>>();

function resolveConfigForm(gameId: string): ComponentType<LobbyConfigFormProps> | null {
  const cached = lazyConfigForms.get(gameId);
  if (cached) return cached;

  const loader = LOBBY_CONFIG_REGISTRY[gameId];
  if (!loader) return null;

  const Component = lazy(loader);
  lazyConfigForms.set(gameId, Component);
  return Component;
}

/** The same "lobby-eligible" mode predicate `GameCard`'s `canHostLobby` uses (docs/games/word-rush/GAME_PLAN.md §13.9) — a non-solo mode whose declared max exceeds 2. */
function lobbyEligibleModes(game: GameCatalogEntry): string[] {
  return game.supportedModes.filter((candidate) => {
    if (candidate === 'solo') return false;
    const counts = game.playerCounts[candidate];
    return !!counts && counts.max > 2;
  });
}

/**
 * The generic create-lobby dialog (docs/games/word-rush/GAME_PLAN.md §11,
 * W1) — platform code with no game-specific conditional (CLAUDE.md §2): it
 * collects up to 9 invitees by username the same way
 * `CreateMultiplayerDialog` does, and renders whatever lobby-settings form
 * the target game registered in `apps/web/src/games/registry.ts`'s
 * `LOBBY_CONFIG_REGISTRY` — falling back to a plain "no extra settings"
 * message for a game with none. On success, navigates to `/play/:sessionId`
 * exactly like every other session-creation flow.
 */
export function CreateLobbyDialog({
  game,
  open,
  onOpenChange,
  onActiveGameConflict,
}: CreateLobbyDialogProps) {
  const navigate = useNavigate();
  const createLobby = useCreateLobbySession();
  const eligibleModes = lobbyEligibleModes(game);
  const [mode, setMode] = useState(eligibleModes[0] ?? '');
  const [config, setConfig] = useState<unknown>(undefined);
  const [inviteeInput, setInviteeInput] = useState('');
  const [invitees, setInvitees] = useState<string[]>([]);

  const ConfigForm = resolveConfigForm(game.gameId);

  function addInvitee() {
    const trimmed = inviteeInput.trim();
    if (!trimmed) return;
    if (invitees.includes(trimmed)) return;
    if (invitees.length >= MAX_INVITEES) return;
    setInvitees((prev) => [...prev, trimmed]);
    setInviteeInput('');
  }

  function removeInvitee(username: string) {
    setInvitees((prev) => prev.filter((u) => u !== username));
  }

  function submit() {
    createLobby.mutate(
      { gameId: game.gameId, mode, config, inviteeUsernames: invitees },
      {
        onSuccess: (session) => {
          onOpenChange(false);
          void navigate(`/play/${session.id}`);
        },
        // Stays open behind the shared conflict dialog so the retry can
        // re-run this mutation from a still-mounted component.
        onError: (error) => onActiveGameConflict?.(error, submit),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Host ${game.name}`}
      description={`Choose settings for this ${modeLabel(mode)} lobby, then optionally invite up to ${MAX_INVITEES} players. Everyone else can join with the code shown once the lobby is created.`}
    >
      <div className="flex flex-col gap-4">
        {eligibleModes.length > 1 && (
          <div className="flex flex-col gap-1">
            <label htmlFor="lobby-mode" className="text-sm font-medium text-textPrimary">
              Mode
            </label>
            <select
              id="lobby-mode"
              value={mode}
              onChange={(event) => setMode(event.target.value)}
              className="rounded-md border border-border bg-surface px-3 py-2 text-base text-textPrimary"
            >
              {eligibleModes.map((candidate) => (
                <option key={candidate} value={candidate}>
                  {modeLabel(candidate)}
                </option>
              ))}
            </select>
          </div>
        )}

        {ConfigForm ? (
          <Suspense fallback={<LoadingState label="Loading settings…" />}>
            {/* eslint-disable-next-line react-hooks/static-components -- documented dynamic
                plugin-component pattern (mirrors GameHostPage.tsx): which form renders here is
                only known at runtime from `game.gameId`, resolved through the per-gameId-cached
                `resolveConfigForm` above rather than re-created every render. */}
            <ConfigForm value={config} onChange={setConfig} />
          </Suspense>
        ) : (
          <p className="text-sm text-textSecondary">No extra settings for this game.</p>
        )}

        <div className="flex flex-col gap-2">
          <label htmlFor="lobby-invitee" className="text-sm font-medium text-textPrimary">
            Invite by username (optional)
          </label>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                id="lobby-invitee"
                label="Username"
                value={inviteeInput}
                onChange={(e) => setInviteeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addInvitee();
                  }
                }}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={addInvitee}
              disabled={invitees.length >= MAX_INVITEES}
            >
              Add
            </Button>
          </div>
          {invitees.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {invitees.map((username) => (
                <li
                  key={username}
                  className="flex items-center gap-1 rounded-full bg-surfaceAlt px-3 py-1 text-sm text-textPrimary"
                >
                  {username}
                  <button
                    type="button"
                    aria-label={`Remove ${username}`}
                    onClick={() => removeInvitee(username)}
                    className="text-textSecondary hover:text-danger"
                  >
                    <Icon name="close" className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {createLobby.isError && !isActiveGameConflict(createLobby.error) && (
          <ErrorState message={getErrorMessage(createLobby.error)} />
        )}

        <Button onClick={submit} isLoading={createLobby.isPending} className="self-start">
          Create lobby
        </Button>
      </div>
    </Dialog>
  );
}
